/* eslint-disable */
import camelcase from "camelize";
import http from "@http";
import config from "@config";
import KeycloakAdapter from "@src/keycloak";
import LocalStorageService from "@services/LocalStorageService";

export const emailPattern = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;

const localStorageService = LocalStorageService.getService();

const getSchema = schemaName => {
  return camelcase(transformRequiredFields(require(`@schemas/${schemaName}.json`)));
};
const parseSchema = async (schemaName, params) => {
  let schema = getSchema(schemaName);
  const links = findByKey(schema, "links", []);
  // Resolve all linked endpoints and wait for them to finish before returning the schema
  if (links && links.length) {
    try {
      await Promise.all(links.map(link => resolveLinks(link, params)));
    } catch (err) {
      // ensure we surface errors during link resolution
      // eslint-disable-next-line no-console
      console.error("Error while resolving schema links for", schemaName, err);
      throw err;
    }
  }

  return schema;
};

async function resolveLinks(schema, params) {
  try {
    const href = `${config.endpointBaseUrl.slice(0, -1)}${parseHref(schema.links[0].href, params)}`;
    const token = localStorageService.getAccessToken() || KeycloakAdapter.getToken();

    const response = await fetch(href, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      const err = new Error(
        `Failed to fetch schema link ${href}: ${response.status} ${response.statusText} ${text}`,
      );
      // eslint-disable-next-line no-console
      console.error(err);
      throw err;
    }

    const json = await response.json();
    populateOneOfs(schema, json?.content || json);
    return schema;
  } catch (err) {
    // Log and rethrow so callers can handle it (sagas should now surface this)
    // eslint-disable-next-line no-console
    console.error("Error resolving schema link", err, schema);
    throw err;
  }
}

const parseHref = (href, params) => {
  const hasParams =
    !!params && !(Object.keys(params).length === 0 && params.constructor === Object);

  if (hasParams) {
    Object.entries(params).forEach(item => {
      const target = "{" + item[0] + "}";

      if (href.includes(target)) {
        href = href.replace(target, item[1]);
      }
    });
  }

  return href;
};

export const transformRequiredFields = obj => {
  return obj
    ? Object.keys(obj).reduce((acc, k) => {
        if (typeof obj[k] === "object" && !Array.isArray(obj[k])) {
          return Object.assign({}, acc, { [k]: transformRequiredFields(obj[k]) });
        }

        if (k === "required" && Array.isArray(obj[k])) {
          return Object.assign({}, acc, { [k]: obj[k].map(s => camelcase(s)) });
        }

        if (typeof obj[k] === "object" && Array.isArray(obj[k])) {
          return Object.assign({}, acc, {
            [k]: obj[k].map(o => (typeof o === "object" ? transformRequiredFields(o) : o)),
          });
        }

        return Object.assign({}, acc, { [k]: obj[k] });
      }, {})
    : obj;
};

const findByKey = (object, key, matches) => {
  var value;
  Object.keys(object).some(function(k) {
    if (k === key) {
      value = object[k];
      matches.push(object);
    }
    if (object[k] && typeof object[k] === "object") {
      value = findByKey(object[k], key, matches);
    }
  });
  return matches;
};

const populateOneOfs = (schema, result) => {
  if (Array.isArray(result)) {
    // Limit the oneOfs to 359 since there is a bug related to the number of records
    // https://github.com/rjsf-team/react-jsonschema-form/issues/3829
    result.splice(0, 300).forEach(item => {
      if (typeof schema.links !== "undefined") {
        delete schema.links;
      }
      if (typeof schema.oneOf === "undefined") {
        schema.oneOf = [];
      }
      schema.oneOf.push({
        const: item.id,
        title: item.name,
      });
    });
  }
};

// Service Desk issue schemas generator
const getSchemaField = formDataField => {
  const _title = formDataField.name;
  const indx = _title.indexOf(" ");
  const title = _title.slice(indx + 1); // +1 is to include the searched string too

  if (formDataField.schema.type === "option") {
    const options = formDataField.allowedValues.map(field => ({
      const: field.id,
      title: field.value,
    }));
    return {
      type: "object",
      required: formDataField.required ? ["id"] : [],
      properties: {
        id: {
          type: "string",
          oneOf: options,
          title,
          withoutPortal: true,
        },
      },
    };
  }

  const privimitiveProperties = {
    string: {
      title,
      type: "string",
    },
    datetime: {
      title,
      type: "string",
      format: "date-time",
    },
    date: {
      title,
      type: "string",
      format: "date",
    },
    number: {
      title,
      type: "number",
    },
    attachment: {
      title,
      type: "array",
      items: {
        type: "string",
        format: "data-url",
      },
    },
  };

  return privimitiveProperties[formDataField.schema.type];
};

const buildSchema = fields => {
  // get required fields and put them in schema template
  const requiredFields = fields.filter(field => field.required).map(field => field.fieldId);
  const schemaTemplate = {
    properties: {},
    type: "object",
    $id: `urn:jsonschema:eu:servicedesk:IssueDto-${Date.now()}`,
    required: requiredFields,
  };

  // generate schema properties
  const schema = fields.reduce((acc, field) => {
    return {
      ...acc,
      properties: {
        ...acc.properties,
        [field.fieldId]: getSchemaField(field),
      },
    };
  }, schemaTemplate);

  return schema;
};

const datePickerOptions = {
  placeholder: "dd/mm/yyyy",
  dateFormat: config.dateFormatBE,
};

const dateTimePickerOptions = {
  enableTime: true,
  dateFormat: "DD/MM/YYYY HH:mm",
  placeholder: "dd/mm/yyyy hh:mm",
};

const buildUiSchemaField = field => {
  const uiOptions = {
    datetime: {
      "ui:widget": "date-picker",
      "ui:options": dateTimePickerOptions,
    },
    date: {
      "ui:widget": "date-picker",
      "ui:options": datePickerOptions,
    },
    string: {
      "ui:widget": "textarea",
    },
    number: {
      "ui:widget": "number-input",
    },
  };

  return uiOptions[field.schema.type];
};

const buildUiSchema = (fields, attachmentFieldName) => {
  // we need to preset some ui options to select/date picker/textarea components so for now we modify only them
  // jira api does not provide proper type for textareas so we need to parse it from the string. Example - "com.atlassian.jira.plugin.system.customfieldtypes:textarea"
  const uiSchemaFields = ["option", "datetime", "date", "number"];
  const filteredFields = fields.filter(
    field =>
      uiSchemaFields.includes(field.schema.type) ||
      (field.schema.custom || "").includes("textarea"),
  );

  return filteredFields.reduce(
    (acc, field) => {
      return {
        ...acc,
        [field.fieldId]: buildUiSchemaField(field),
      };
    },
    { "ui:order": attachmentFieldName ? ["*", attachmentFieldName] : ["*"] },
  );
};

const generateServiceDeskSchemas = (formData, withoutAttach) => {
  if (!formData) return [];
  // filter out unnecessary fields - employeeId, clientId and client name BE will pre-fill from user jwt token so we don't need them
  // BE provide sd_ prefix for every field that needs to be included to the form + we add attachment
  const prefix = "sd_";
  const fields = formData.fields
    .filter(
      field =>
        field.name.includes(prefix) &&
        field.name !== "gscClientId" &&
        field.name !== "gscEmployeeId" &&
        field.name !== "gscClientName" &&
        field.name !== "gscEmployeeName" &&
        (!withoutAttach || !field.schema.custom?.includes("attachment")),
    )
    .map(field => {
      const name = field.name.replace(/^sd_/, "");
      if (field.schema.custom.includes("attachment")) {
        return {
          ...field,
          name,
          schema: {
            ...field.schema,
            type: "attachment",
          },
        };
      }

      return { ...field, name };
    });

  // we need to find attachment field to place it on the bottom in uiSchema
  const attachmentFieldName = fields.find(el => el.schema.type === "attachment")?.fieldId;
  const schema = buildSchema(fields);
  const uiSchema = buildUiSchema(fields, attachmentFieldName);

  return [schema, uiSchema];
};

export { parseSchema, getSchema, generateServiceDeskSchemas };
